/**
 * Orchestrator — face mesh → smile region → lip crop → tooth outlines.
 */

import { detectFaceLandmarks } from "./faceDetection";
import { toPixelCoords } from "./landmarkProcessing";
import { extractSmileRegion } from "./smileRegionExtraction";
import {
  extractLipCrop,
  prepareToothDetectionCanvas,
  scaleToothResultToCrop,
  clipTeethToInnerLip,
} from "./lipCropExtractor";
import { detectTeeth, setToothDetector, createHeuristicToothDetector } from "./toothDetection";
import { detectTeethTwoPass } from "./twoPassToothDetection";
import { deriveDentalGuides } from "./dentalGuides";
import type {
  FaceDetectionResult,
  LipCropResult,
  ToothDetectionResult,
} from "./types";

// In-browser CV heuristic tooth detector
setToothDetector(createHeuristicToothDetector());

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load source image"));
    img.src = src;
  });
}

export async function analyzeFaceSmile(
  imageSrc: string
): Promise<FaceDetectionResult> {
  const img = await loadImage(imageSrc);
  const width = img.naturalWidth || img.width;
  const height = img.naturalHeight || img.height;

  const landmarks = await detectFaceLandmarks(img);
  if (!landmarks) {
    return {
      landmarks: [],
      smileRegion: null,
      imageWidth: width,
      imageHeight: height,
      detected: false,
    };
  }

  const pixel = toPixelCoords(landmarks, width, height);
  const smileRegion = extractSmileRegion(pixel, width, height);

  return {
    landmarks,
    smileRegion,
    imageWidth: width,
    imageHeight: height,
    detected: true,
  };
}

export async function runDentalAccuracyPipeline(imageSrc: string): Promise<{
  face: FaceDetectionResult;
  lipCrop: LipCropResult | null;
  teeth: ToothDetectionResult | null;
}> {
  const face = await analyzeFaceSmile(imageSrc);
  if (!face.detected || !face.smileRegion) {
    return { face, lipCrop: null, teeth: null };
  }

  const lipCrop = await extractLipCrop(imageSrc, face.smileRegion);
  const { canvas, scale, mouthMask } = await prepareToothDetectionCanvas(lipCrop);
  let teeth = await detectTeethTwoPass(canvas, {
    mouthMask,
    maxTeeth: 16,
  });
  teeth = scaleToothResultToCrop(teeth, scale);
  teeth = {
    ...teeth,
    teeth: clipTeethToInnerLip(teeth.teeth, lipCrop.innerPolygonLocal),
  };

  if (teeth && !teeth.guides) {
    teeth = {
      ...teeth,
      guides: deriveDentalGuides(teeth.teeth, lipCrop.width, lipCrop.height),
    };
  }

  return { face, lipCrop, teeth };
}

export * from "./types";
export * from "./constants";
export { getFaceLandmarker, disposeFaceLandmarker, detectFaceLandmarks } from "./faceDetection";
export {
  toPixelCoords,
  getOuterLipPoints,
  getInnerLipPoints,
  orderPolygonClockwise,
} from "./landmarkProcessing";
export {
  extractSmileRegion,
  convexHull,
  boundingBoxFromPoints,
} from "./smileRegionExtraction";
export {
  renderSmileOverlay,
  renderToothOutlines,
  clearCanvas,
  drawPolygon,
} from "./canvasRenderer";
export {
  extractLipCrop,
  lipCropToCanvasAsync,
  prepareToothDetectionCanvas,
  scaleToothResultToCrop,
  clipTeethToInnerLip,
  expandPolygon,
} from "./lipCropExtractor";
export {
  detectTeeth,
  setToothDetector,
  getToothDetector,
  createHeuristicToothDetector,
} from "./toothDetection";
export { deriveDentalGuides, yOnArc, derivePapillaRegions, derivePerToothGingivalArcs, toothGingivalPoint } from "./dentalGuides";
export {
  DEFAULT_TOOTH_EDIT_POINTS,
  MAX_TOOTH_EDIT_POINTS,
  MIN_TOOTH_RESIZE_WIDTH,
  MIN_TOOTH_RESIZE_HEIGHT,
  MIN_ARCH_RESIZE_WIDTH,
  MIN_ARCH_RESIZE_HEIGHT,
  resampleClosedContour,
  normalizeTeethForEditing,
  insertContourPoint,
  removeContourPoint,
  removeLastContourPoint,
  translateToothOutline,
  toothBoundingBox,
  mapContourBetweenBoxes,
  bboxFromResizeDrag,
  resizeToothOutline,
  mirrorBoundingBox,
  archBoundingBox,
  bboxFromArchCornerDrag,
  resizeArchTeeth,
  resizeArchGuides,
  buildArchSmileOrigin,
  archSmileTargetFromDrag,
  stretchArchTeethSmile,
  stretchArchGuidesSmile,
  createManualToothOutline,
  upperArchFdiForCount,
  assignUpperArchFdi,
  upperArchSlotCenterX,
  nextMissingUpperArchFdi,
  sortTeethByUpperArch,
} from "./contourEdit";
export type {
  ToothResizeCorner,
  ToothResizeEdge,
  ToothResizeMode,
  ArchCorner,
  ArchEdge,
  ArchHandle,
  ArchSmileOrigin,
  ArchSmileTarget,
} from "./contourEdit";
export { detectToothInRegion } from "./detectToothInRegion";
export {
  detectTeethTwoPass,
  locateAllTeeth,
  markToothBorders,
  refineToothInMark,
  clipContourToXRange,
  enforceNonOverlappingTeeth,
} from "./twoPassToothDetection";
export type { ToothMark, TwoPassToothOptions } from "./twoPassToothDetection";
export {
  generateOutlineImage,
} from "./generateOutlineImage";
export { traceSmileWireframe, renderWireframeFromContours } from "./traceToothWireframe";
export type { TraceWireframeResult, TracedTooth } from "./traceToothWireframe";
export type {
  OutlineImageOptions,
  OutlineImageResult,
} from "./generateOutlineImage";
export {
  generateToothOverlayImage,
} from "./generateToothOverlayImage";
export type {
  ToothOverlayOptions,
  ToothOverlayResult,
} from "./generateToothOverlayImage";
export {
  compositeFilterOnBefore,
  prepareFilterPlacement,
  compositePlacementOnBefore,
  placementLeftWidth,
  placementRightWidth,
  placementLeftHeight,
  placementRightHeight,
  clampMidlineGuide,
  midlineGuideFromDetection,
  snapPlacementToMidline,
  WIREFRAME_FRAME_INSET,
} from "./overlayFilterOnBeforeLocal";
export type {
  FilterOverlayOptions,
  FilterOverlayResult,
  FilterPlacementResult,
  WireframePlacement,
  FacialMidlineGuide,
} from "./overlayFilterOnBeforeLocal";
export {
  placementWorldAabb,
  warpFilterSmilePerspective,
  warpFilterIndependentSides,
  DEFAULT_ARCH_GEOMETRY,
  FLAT_ARCH_GEOMETRY,
} from "./wireframeToothEdit";
export type { ArchGeometryOptions } from "./wireframeToothEdit";
export {
  WIRE_TOOTH_DEFAULT_POINTS,
  WIRE_TOOTH_MAX_POINTS,
  wireToothContour,
  wireToothHandlePoints,
  moveWireToothHandle,
  addWireToothHandle,
  removeWireToothHandle,
  resetWireTooth,
  renderWireTeethFilter,
  buildWireTeethFromContours,
  setWireToothTransform,
  isWireToothEdited,
  wireToothCenter,
  wireToothLocalBounds,
  wireToothDisplayBounds,
  resizeWireToothByHandle,
  moveWireTooth,
  TOOTH_SCALE_MIN,
  TOOTH_SCALE_MAX,
} from "./wireframeTeethShape";
export type { ToothBox, ToothSizeHandle } from "./wireframeTeethShape";
export type { WireTooth, WireTeethModel } from "./wireframeTeethShape";
export {
  paintEditedAfter,
  compositeEditedAfter,
  compositeOutlinesOnAfter,
} from "./afterToothOverlay";
export { filterPointToBefore, beforePointToFilter } from "./wireframeDisplayMapping";
export type { WireDisplayContext } from "./wireframeDisplayMapping";
export {
  prepareSmileSuperimpose,
  bakeSmileSuperimpose,
  similarityFromPoints,
} from "./superimposeSmile";
export type {
  SuperimposePrepareResult,
  SuperimposeBakeOptions,
  SimilarityTransform,
} from "./superimposeSmile";
